import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as authApi from '@/api/auth'
import * as correctionsApi from '@/api/corrections'
import * as notificationsApi from '@/api/notifications'
import { setApiLocale } from '@/api/client'
import { hasSession } from '@/api/session'
import { isAppLocale, type AppLocale } from '@/locales'

export type Theme = 'light' | 'dark'

// 언어 목록은 생성물에서 온다 — 여기 적어 두면 번역을 늘려도 화면에 안 뜬다.
export type { AppLocale }

const THEME_KEY = 'asta.theme'
const LOCALE_KEY = 'asta.locale'

/** 폴링 주기. Twig 쪽 배지와 같은 값이어야 놀라지 않는다. */
const POLL_MS = 60_000

/**
 * 화면 상태 — 테마, 언어, 사이드바, 알림 배지.
 *
 * 배지 폴링은 **스토어에서 `setInterval` 로** 돈다. 컴포넌트 스코프 타이머
 * (`useIntervalFn`)를 쓰면 그 컴포넌트가 언마운트될 때 전역 폴링이 영구 정지한다
 * (CLAUDE.md Vue 규약).
 */
export const useUiStore = defineStore('ui', () => {
  const theme = ref<Theme>(readTheme())
  const locale = ref<AppLocale>(readLocale())
  /** 이 브라우저에서 언어를 고른 적이 있는가. 계정 설정보다 우선한다. */
  let chosenHere = null !== read(LOCALE_KEY)
  const sidebarCollapsed = ref(readBoolean('asta.sidebarCollapsed'))
  const sidebarOpen = ref(false)

  const unreadNotifications = ref(0)
  const pendingCorrections = ref(0)
  const badgeTotal = computed(() => unreadNotifications.value + pendingCorrections.value)

  let timer: ReturnType<typeof setInterval> | null = null

  function applyTheme(): void {
    document.documentElement.dataset.theme = theme.value
  }

  function toggleTheme(): void {
    theme.value = 'dark' === theme.value ? 'light' : 'dark'
    persist(THEME_KEY, theme.value)
    applyTheme()
  }

  /**
   * 화면에서 고른 언어.
   *
   * 세 곳에 반영한다. 브라우저 저장(다음 방문) · API 헤더(서버가 번역해 주는 상태
   * 배지·오류 문구) · **직원 레코드**(다른 기기·다음 로그인). 레코드를 빼먹으면
   * 관리자 매뉴얼이 약속한 "다음 로그인에도 이어진다" 가 거짓이 된다.
   */
  function setLocale(next: AppLocale): void {
    applyLocale(next)
    chosenHere = true

    // 로그인 화면에서도 언어를 바꾼다. 그때는 저장할 레코드가 없다.
    if (hasSession()) {
      // 실패해도 화면 언어는 이미 바뀌었다. 다음 변경에서 다시 시도된다.
      void authApi.updateLocale(next).catch(() => {})
    }
  }

  /**
   * 로그인한 계정의 언어를 따른다.
   *
   * **계정에 저장된 결정이 이긴다.** 서버의 `LocalePreference::seedForLogin()` 과
   * 같은 규칙이라 두 프론트가 갈리지 않는다:
   *
   *   1. `user.locale` 이 있으면 그것 — 이 브라우저에서 고른 것도 덮는다
   *   2. `null`(아직 고른 적 없음)이고 이 브라우저에서 고른 것이 있으면
   *      그것을 **첫 결정으로 레코드에 저장**한다
   *   3. 둘 다 없으면 기본값 그대로 둔다
   *
   * 로그인 전 선택이 이기게 두면 공용 PC 에서 로그인했을 때 내 언어 설정이 조용히
   * 덮어써진다 — 계정에 남은 결정이 그 자리에서 누른 것보다 그 사람의 의도에 가깝다.
   */
  function adoptAccountLocale(accountLocale: string | null): void {
    if (isAppLocale(accountLocale)) {
      // 계정의 결정을 이 브라우저에도 굳힌다. 이제 이것이 이 기기의 선택이다.
      chosenHere = true
      if (accountLocale !== locale.value) {
        applyLocale(accountLocale)
      }

      return
    }

    if (chosenHere) {
      // 첫 결정. 실패해도 화면은 이미 맞으므로 다음 변경에서 다시 시도된다.
      void authApi.updateLocale(locale.value).catch(() => {})
    }
  }

  function applyLocale(next: AppLocale): void {
    locale.value = next
    persist(LOCALE_KEY, next)
    setApiLocale(next)
    document.documentElement.lang = next
  }

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value
    persist('asta.sidebarCollapsed', String(sidebarCollapsed.value))
  }

  /** 좁은 화면의 서랍. 접힘 상태와 달리 **저장하지 않는다**. */
  function toggleDrawer(force?: boolean): void {
    sidebarOpen.value = force ?? !sidebarOpen.value
  }

  async function refreshBadges(canApprove: boolean): Promise<void> {
    try {
      const [notifications, inbox] = await Promise.all([
        notificationsApi.unreadCount(),
        canApprove ? correctionsApi.inboxCounts() : Promise.resolve(null),
      ])
      unreadNotifications.value = notifications.unread
      pendingCorrections.value = null === inbox ? 0 : (inbox.pending ?? 0)
    } catch {
      // 배지 실패는 화면을 막지 않는다. 다음 주기에 다시 시도한다.
    }
  }

  function startPolling(canApprove: boolean): void {
    stopPolling()
    void refreshBadges(canApprove)
    timer = setInterval(() => void refreshBadges(canApprove), POLL_MS)
  }

  function stopPolling(): void {
    if (null !== timer) {
      clearInterval(timer)
      timer = null
    }
    unreadNotifications.value = 0
    pendingCorrections.value = 0
  }

  // 부팅 시 저장된 값을 즉시 반영한다. 안 하면 첫 프레임이 항상 라이트 테마다.
  applyTheme()
  setApiLocale(locale.value)

  return {
    theme,
    locale,
    sidebarCollapsed,
    sidebarOpen,
    unreadNotifications,
    pendingCorrections,
    badgeTotal,
    applyTheme,
    toggleTheme,
    setLocale,
    adoptAccountLocale,
    toggleSidebar,
    toggleDrawer,
    refreshBadges,
    startPolling,
    stopPolling,
  }
})

function readTheme(): Theme {
  const stored = read(THEME_KEY)
  if ('dark' === stored || 'light' === stored) {
    return stored
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * 기본은 **영어**다. 시스템 로케일을 따르지 않는다(사용자 결정).
 *
 * 저장값이 있다 = 이 브라우저에서 언어를 고른 적이 있다. `setLocale()` 만 저장하므로
 * "고르지 않아서 영어" 와 "영어를 골랐다" 를 구분할 수 있고, 그래야 계정에 저장된
 * 언어를 언제 따를지 판단할 수 있다.
 */
function readLocale(): AppLocale {
  const stored = read(LOCALE_KEY)

  // 저장값이 더 이상 지원하지 않는 언어일 수 있다(언어를 뺐을 때). 영어로 되돌린다.
  return isAppLocale(stored) ? stored : 'en'
}

function readBoolean(key: string): boolean {
  return 'true' === read(key)
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* 저장 실패는 무시한다 */
  }
}
